import React from 'react';

import { CompetitionsDropdownType, IMultisportCompetition, SportTypes } from '../../../../../packages/types';
import { SMPArrowDownIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPArrowDownIcon';
import { MobileModal } from '@velinsi/smp-widgets-ui/src/molecules/MobileModal';
import { getIconAsBgImage, getLabelValue } from '@sportal365/smp-widget-utils';
import useDetectClickOutside from '../../../../../packages/hooks/useDetectClickOutside';
import { SMPContainer, SMPFlex, SMPSportIcon, SMPText } from '@velinsi/smp-widgets-ui';
import { MobileCompetitionGroup } from './MobileCompetitionGroup';
import { MobileSportsGroup } from './MobileSportsGroup';

export type DropdownOptionsProps = {
  options: IMultisportCompetition[];
  selectedCompetition?: CompetitionsDropdownType;
  onChange?: (value: CompetitionsDropdownType) => void;
  topEventsOption: CompetitionsDropdownType;
  closeDropdown: () => void;
};

export type SelectOptionsProps = {
  options: IMultisportCompetition[];
  selectedOption?: CompetitionsDropdownType | undefined;
  topEventsOption: CompetitionsDropdownType;
  handleChange?: (value: CompetitionsDropdownType) => void;
  close: () => void;
};

export const MobileCompetitionsDropdown = ({
  selectedCompetition,
  options,
  onChange,
  topEventsOption,
  closeDropdown,
}: DropdownOptionsProps) => {
  const { isComponentVisible, setIsComponentVisible, ref } = useDetectClickOutside<HTMLDivElement>(true);

  const toggleShowDropdown = () => {
    setIsComponentVisible((prevValue) => !prevValue);
  };

  return (
    <SMPContainer
      ref={ref}
      style={{
        margin: 0,
        padding: 0,
        width: '100%',
        borderRadius: '6px',
        position: 'relative',
      }}
    >
      <SMPFlex
        onClick={toggleShowDropdown}
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          position: 'relative',
          padding: '12px 9px',
          color: 'var(--colors-hiContrast)',
          cursor: 'pointer',
          fontWeight: 'bold',
          lineHeight: '20px',
          border: '1px solid',
          backgroundColor: 'var(--colors-pillBackgroundColor)',
          borderRadius: '6px',
          borderColor: isComponentVisible !== false ? 'var(--colors-multisportHighlightColor)' : 'var(--colors-dropdownBorderColor)',
        }}
      >
        <SMPFlex style={{ gap: '8px', alignItems: 'center' }}>
          {selectedCompetition?.sport === SportTypes.ALL && <SMPSportIcon sport={SportTypes.ALL} />}
          {selectedCompetition?.logo && (
            <SMPContainer
              style={{
                padding: 0,
                margin: 0,
                backgroundImage: `url("${selectedCompetition?.logo}")`,
                height: '20px',
                width: '20px',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
          )}
          {!selectedCompetition?.logo && selectedCompetition?.sport !== SportTypes.ALL && (
            <SMPContainer
              style={{
                padding: 0,
                margin: 0,
                backgroundImage: `url("${getIconAsBgImage('competition_placeholder')}")`,
                height: '20px',
                width: '20px',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
          )}
          <SMPText css={{ lineHeight: '20px', fontWeight: 'bold', fontSize: '14px' }}>
            {selectedCompetition ? selectedCompetition.label : 'Select Competition'}
          </SMPText>
        </SMPFlex>
        <SMPArrowDownIcon
          style={{
            color: isComponentVisible ? 'var(--colors-multisportHighlightColor)' : 'var(--colors-selectButtonSecondaryColor)',
            transform: isComponentVisible ? 'rotate(180deg)' : 'unset',
          }}
        />
      </SMPFlex>

      <MobileModal
        isOpen={isComponentVisible}
        onClose={() => {
          setIsComponentVisible(false);
          closeDropdown();
        }}
      >
        <DropdownOptions
          options={options}
          topEventsOption={topEventsOption}
          handleChange={onChange}
          close={toggleShowDropdown}
          selectedOption={selectedCompetition}
        />
      </MobileModal>
    </SMPContainer>
  );
};

const DropdownOptions = ({ options, selectedOption, topEventsOption, handleChange, close }: SelectOptionsProps) => {
  return (
    <>
      <MobileCompetitionGroup
        onClick={() => {
          handleChange && handleChange(topEventsOption);
          close();
        }}
        name={getLabelValue(topEventsOption.label, 'Top Events')}
        topEvents
        isSelected={selectedOption?.value === topEventsOption.value}
        isLast={true}
      />
      {options.map((sportGroup) => {
        if (sportGroup.sport === SportTypes.ALL) return null;
        return (
          <React.Fragment key={sportGroup.sport}>
            <MobileSportsGroup sportGroup={sportGroup} />
            {sportGroup.competitions.map((compEntry, index) => (
              <MobileCompetitionGroup
                isLast={index === sportGroup.competitions.length - 1}
                isSelected={selectedOption?.value === compEntry.competition.id}
                onClick={() => {
                  handleChange &&
                    handleChange({
                      value: compEntry.competition.id,
                      label: compEntry.competition.name,
                      sport: compEntry.competition.sport as SportTypes,
                      logo: compEntry.competition.logoUrl,
                    });
                  close();
                }}
                name={compEntry.competition.name}
                eventCount={compEntry.eventCount}
                logo={compEntry.competition.logoUrl}
                key={compEntry.competition.id}
              />
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
};
